/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";

import Stack from "@mui/material/Stack";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import FlagIcon from "@mui/icons-material/Flag";
import EmojiFlagsIcon from "@mui/icons-material/EmojiFlags";
import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useSnackbar } from "notistack";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { LoadingButton } from "@mui/lab";
import { SALES_INVOICE_ADD, salesInvoiceEdit } from "app/routes/constant";
import {
  deleteSalesInvoice,
  getSalesInvoiceList,
  updateSalesInvoiceFlag,
} from "app/redux/slice/salesInvoiceSlice";
import { getPlatformList } from "app/redux/slice/platformSlice";
import Span from "@jumbo/shared/Span";
import { getVendorList } from "app/redux/slice/vendorSlice";
import { FlagCircle, WarningAmber } from "@mui/icons-material";
import { resetFlagsApi } from "app/redux/slice/commonSlice";

const SalesInvoice = () => {
  const navigate = useNavigate();

  const { stageAction } = useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();
  const [showFlag, setShowFlag] = useState(false);

  const salesInvoiceData = useSelector((state) => state?.salesInvoice);
  const platformData = useSelector((state) => state?.platform);
  const supplierData = useSelector((state) => state?.vendor);

  // Initialize state from localStorage
  const getInitialState = () => {
    const savedState = localStorage.getItem("sales-invoice-filters");
    if (savedState) {
      try {
        return JSON.parse(savedState);
      } catch (error) {
        console.error("Error parsing saved state:", error);
      }
    }
    return {
      searchText: "",
      order: "desc",
      orderBy: "createdAt",
      platformId: null,
      vendorId: null,
      paymentStatus: "All",
      flagStatus: "All",
      showDeleted: false,
    };
  };

  const [searchText, setSearchText] = useState(getInitialState().searchText);
  const [order, setOrder] = useState(getInitialState().order);
  const [orderBy, setOrderBy] = useState(getInitialState().orderBy);
  const [platformId, setPlatformId] = useState(getInitialState().platformId);
  const [vendorId, setVendorId] = useState(getInitialState().vendorId);
  const [paymentStatus, setPaymentStatus] = useState(
    getInitialState().paymentStatus
  );
  const [flagStatus, setFlagStatus] = useState(getInitialState().flagStatus);
  const [showDeleted, setShowDeleted] = useState(getInitialState().showDeleted);

  const onConfirmFlagReset = () => {
    setShowFlag(false);
    dispatch(resetFlagsApi("Invoice"))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "invoice" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });

  // Save state to localStorage whenever filters change
  const saveStateToLocalStorage = (newState) => {
    const stateToSave = {
      searchText: newState.searchText || searchText,
      order: newState.order || order,
      orderBy: newState.orderBy || orderBy,
      platformId:
        newState.platformId !== undefined ? newState.platformId : platformId,
      vendorId: newState.vendorId !== undefined ? newState.vendorId : vendorId,
      paymentStatus: newState.paymentStatus || paymentStatus,
      flagStatus: newState.flagStatus || flagStatus,
      showDeleted:
        newState.showDeleted !== undefined ? newState.showDeleted : showDeleted,
    };
    localStorage.setItem("sales-invoice-filters", JSON.stringify(stateToSave));
  };

  // Update search text and save to localStorage
  const handleSearchTextChange = (value) => {
    setSearchText(value);
    saveStateToLocalStorage({ searchText: value });
  };

  // Update platform and save to localStorage
  const handlePlatformChange = (value) => {
    setPlatformId(value);
    saveStateToLocalStorage({ platformId: value });
  };

  // Update vendor and save to localStorage
  const handleVendorChange = (value) => {
    setVendorId(value);
    saveStateToLocalStorage({ vendorId: value });
  };

  // Update payment status and save to localStorage
  const handlePaymentStatusChange = (value) => {
    setPaymentStatus(value);
    saveStateToLocalStorage({ paymentStatus: value });
  };

  // Update flag status and save to localStorage
  const handleFlagStatusChange = (value) => {
    setFlagStatus(value);
    saveStateToLocalStorage({ flagStatus: value });
  };

  // Update show deleted and save to localStorage
  const handleShowDeletedChange = (value) => {
    setShowDeleted(value);
    saveStateToLocalStorage({ showDeleted: value });
  };

  // Clear all filters and save to localStorage
  const clearAllFilters = () => {
    const clearedState = {
      searchText: "",
      platformId: null,
      vendorId: null,
      paymentStatus: "All",
      flagStatus: "All",
      showDeleted: false,
    };
    setSearchText(clearedState.searchText);
    setPlatformId(clearedState.platformId);
    setVendorId(clearedState.vendorId);
    setPaymentStatus(clearedState.paymentStatus);
    setFlagStatus(clearedState.flagStatus);
    setShowDeleted(clearedState.showDeleted);
    saveStateToLocalStorage(clearedState);
  };

  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      const rolePermission = permissionData;
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === "create-invoice") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-invoice") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-invoice") {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  const columns = [
    { id: "invoiceNumber", label: "Invoice No", sort: true },
    {
      id: "flag",
      label: "Flag",
      sort: false,
      align: "center",
      render: (elm) => {
        const isFlagged = elm?.flag || false;
        return (
          <IconButton
            disabled={!permission.updatePermission}
            onClick={() => {
              const newValue = !isFlagged;
              dispatch(
                updateSalesInvoiceFlag({
                  id: elm?._id,
                  data: { flag: newValue },
                })
              )
                .unwrap()
                .then((res) => {
                  if (res?.status === 200) {
                    enqueueSnackbar("Flag updated successfully", {
                      variant: "success",
                    });
                    getData();
                  } else {
                    enqueueSnackbar(
                      res?.data?.responseMessage || "Update failed",
                      { variant: "error" }
                    );
                  }
                });
            }}
          >
            {isFlagged ? (
              <FlagIcon color="primary" />
            ) : (
              <EmojiFlagsIcon color="disabled" />
            )}
          </IconButton>
        );
      },
    },
    {
      id: "invoiceDate",
      minWidth: 115,
      label: "Date",
      sort: true,
      render: (row) => moment(row?.invoiceDate).format("DD-MM-YYYY"),
    },
    {
      id: "platformId",
      label: "Platform",
      render: (row) => row?.platformId?.name,
      minWidth: 110,
    },
    {
      id: "vendorId",
      label: "Vendor",
      render: (row) => row?.vendorId?.name,
      minWidth: 110,
    },
    {
      id: "totalAmount",
      label: "Amount",
      minWidth: 100,
      isTotal: true,
      format: (value) => value?.toFixed(2),
    },
    {
      id: "receivedAmount",
      label: "Received Amount",
      minWidth: 100,
      isTotal: true,
      format: (value) => value?.toFixed(2),
    },
    {
      id: "dueAmount",
      label: "Due Amount",
      minWidth: 100,
      isTotal: true,
      format: (value) => value?.toFixed(2),
    },
    {
      id: "paymentStatus",
      label: "Payment Status",
      align: "center",
      render: (row) =>
        row?.paymentStatus === 2 ? (
          <Span style={{ color: "green" }}>Paid</Span>
        ) : row?.paymentStatus === 1 ? (
          <Span style={{ color: "#d77700" }}>Partial Paid</Span>
        ) : (
          <Span style={{ color: "red" }}>Pending</Span>
        ),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "center",
      render: (row) => (
        <>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              navigate(salesInvoiceEdit(row?._id));
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            disabled={!permission.deletePermission}
            onClick={() => {
              deleteStageAction(row);
            }}
          >
            <DeleteIcon color={"error"} />
          </IconButton>
        </>
      ),
    },
  ];
  const deletedColumns = [
    { id: "invoiceNumber", label: "Invoice No", sort: true },
    {
      id: "flag",
      label: "Flag",
      sort: false,
      align: "center",
      render: (elm) => {
        const isFlagged = elm?.flag || false;
        return (
          <IconButton>
            {isFlagged ? (
              <FlagIcon color="primary" />
            ) : (
              <EmojiFlagsIcon color="disabled" />
            )}
          </IconButton>
        );
      },
    },
    {
      id: "invoiceDate",
      minWidth: 115,
      label: "Date",
      sort: true,
      render: (row) => moment(row?.invoiceDate).format("DD-MM-YYYY"),
    },
    {
      id: "platformId",
      label: "Platform",
      render: (row) => row?.platformId?.name,
      minWidth: 110,
    },
    {
      id: "totalAmount",
      label: "Amount",
      minWidth: 100,
    },
  ];

  const DeleteInvoice = () => {
    closeDelete();
    dispatch(deleteSalesInvoice(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  const getData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(platformId && { platformId: platformId?._id }),
      ...(vendorId && { vendorId: vendorId?._id }),
      ...(paymentStatus !== "All" && {
        paymentStatus,
      }),
      ...(flagStatus !== "All" && { flag: flagStatus === "true" }),
      ...(showDeleted && { deletedAt: true }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }

    dispatch(getSalesInvoiceList(value));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
    saveStateToLocalStorage({ order: Order, orderBy: OrderBy });
  };

  const handleSubmit = () => {
    handleChangePage(0, 0);
    getData();
  };

  useEffect(() => {
    getPermission();
    dispatch(getPlatformList({ sort: 1, field: "name" }));
    dispatch(getVendorList({ sort: 1, field: "name" }));
  }, []);

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy, showDeleted]);

  return (
    <>
      {salesInvoiceData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => handleSearchTextChange(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => handleSearchTextChange("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <JumboAutoComplete
                      options={platformData?.list || []}
                      label="Platform"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`platformId`}
                      value={platformId}
                      onChange={(evt, value) => handlePlatformChange(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <JumboAutoComplete
                      options={supplierData?.list || []}
                      label="Vendor"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`vendorId`}
                      value={vendorId}
                      onChange={(evt, value) => handleVendorChange(value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Status</InputLabel>
                      <Select
                        name="isInvoiceCreated"
                        id="isInvoiceCreated"
                        value={paymentStatus}
                        label="Payment Status"
                        size="small"
                        onChange={(e) =>
                          handlePaymentStatusChange(e.target.value)
                        }
                      >
                        <MenuItem value={"All"}>All</MenuItem>
                        <MenuItem value={"0"}>Pending</MenuItem>
                        <MenuItem value={"1"}>Partial Paid</MenuItem>
                        <MenuItem value={"2"}>Paid</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Flag</InputLabel>
                      <Select
                        value={flagStatus}
                        label="Flag"
                        size="small"
                        onChange={(e) => handleFlagStatusChange(e.target.value)}
                      >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="true">Flagged</MenuItem>
                        <MenuItem value="false">Unflagged</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Invoice List
                </Typography>

                <Div sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Show Deleted
                  </Typography>
                  <Switch
                    checked={showDeleted}
                    onChange={(e) => handleShowDeletedChange(e.target.checked)}
                    color="primary"
                  />
                  {permission.updatePermission && (
                    <Tooltip title="Reset All Flags">
                      <IconButton onClick={() => setShowFlag(true)}>
                        <FlagCircle color="primary" sx={{ fontSize: 30 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={() => {
                        navigate(SALES_INVOICE_ADD);
                      }}
                      sx={{
                        mt: { xs: 1, md: 0 },
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Add Invoice
                    </LoadingButton>
                  )}
                </Div>
              </Stack>
              <Table
                columns={showDeleted ? deletedColumns : columns}
                rows={salesInvoiceData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={salesInvoiceData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
                showTotalInFooter={true}
              />
            </CardContent>
          </Card>
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete the sales invoice?"
            onConfirm={DeleteInvoice}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
          <Dialog
            open={showFlag}
            onClose={() => setShowFlag(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                pb: 0,
              }}
            >
              <WarningAmber sx={{ color: "error.main", fontSize: 28 }} />
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Reset All Flags?
              </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body1">
                This will reset all flags on this page. Do you want to continue?
              </Typography>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
              <Button
                onClick={() => setShowFlag(false)}
                variant="outlined"
                color="inherit"
              >
                Cancel
              </Button>
              <Button
                onClick={onConfirmFlagReset}
                variant="contained"
                color="error"
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default SalesInvoice;
